import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import Button from '@ui/Button';
import { updateUserByKey } from '@util/firestore/users';
import { isPOBoxAddress } from '@util/address';
import { AddressDocument } from '@util/types/firestore/address';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import BaseModal from './BaseModal';
import { useStripeContext } from 'context/StripeContext';
import { STRIPE_ELEMENT_OPTIONS } from '@util/get-stripe';
import StripeAddressForm, {
  StripeAddressChangeEvent,
} from '@c/forms/StripeAddressForm';
import { isMobile } from '@util/index';

interface AddressFormProps {
  dismiss: (address?: AddressDocument) => void;
  address?: AddressDocument;
  /** Index of `address` within userDoc.addresses (dashboard edit path only) — write target is matched by index, never by address_line1 (two addresses can share line1). */
  index?: number;
  dontUpdateUserDoc?: boolean;
}
const AddressForm = ({
  dismiss,
  address,
  index,
  dontUpdateUserDoc,
}: AddressFormProps) => {
  const { userDoc } = useAuth();
  const queryClient = useQueryClient();
  const { showErrorToast } = useToastContext();
  const elements = useElements();
  const stripe = useStripe();
  const [verifiedAddress, setVerifiedAddress] =
    React.useState<AddressDocument | null>(null);

  const [validating, setValidating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  // GB web#1743. Set only when ShipEngine couldn't verify the address
  // exactly as typed but a normalized variant of it does verify (CA
  // fractional house number / hyphenated postal code). Bill's ruling
  // 2026-08-21: never auto-accept either form - the user must explicitly
  // choose, shown side-by-side below. Copy is Bill's own proposed working
  // draft (his mail, 2026-08-21); still needs his verbatim sign-off before
  // merge, per the standing copy-approval rule.
  const [confirmChoice, setConfirmChoice] = React.useState<{
    typed: AddressDocument;
    alternative: AddressDocument;
  } | null>(null);

  const handleChange = (e: StripeAddressChangeEvent) => {
    if (e.status === 'validating') {
      setValidating(true);
      setConfirmChoice(null);
      return;
    }
    setValidating(false);
    if (e.status === 'valid') {
      setVerifiedAddress(e.result);
      setConfirmChoice(null);
    } else if (e.status === 'needs_confirmation') {
      setVerifiedAddress(null);
      setConfirmChoice({ typed: e.typed, alternative: e.alternative });
    } else {
      setVerifiedAddress(null);
      setConfirmChoice(null);
    }
  };

  const chooseAddress = (chosen: AddressDocument) => {
    setVerifiedAddress(chosen);
    setConfirmChoice(null);
  };

  const handleSubmit = async () => {
    if (!stripe || !elements || !userDoc) return;

    const addressElement = elements.getElement('address');

    if (!addressElement) return;
    if (!verifiedAddress) return;

    verifiedAddress.is_default = address?.is_default ?? false;

    if (dontUpdateUserDoc) {
      dismiss(verifiedAddress);
      return;
    }

    const addresses = userDoc.addresses ?? [];
    const existingIndex = address
      ? typeof index === 'number'
        ? index
        : addresses.findIndex(
            (a) => a.address_line1 === address.address_line1
          )
      : -1;
    const existingAddress =
      existingIndex >= 0 ? addresses[existingIndex] : undefined;

    // PO Box detection is a pure regex — no network call. ShipEngine
    // validation of the address itself already happened in
    // StripeAddressForm (blocks on error/unverified before Save is even
    // enabled), so all that's left here is the PO-Box-can't-be-default rule.
    const poBox = isPOBoxAddress(verifiedAddress);

    // Editing the CURRENT default into a PO Box would otherwise bypass the
    // make-primary gate (which blocks PO Boxes from becoming the main
    // address) — block it here too.
    if (poBox && existingAddress?.is_default) {
      showErrorToast(
        "This is a PO Box, which can't be your main address. Set a different address as your main address first, then edit this one."
      );
      return;
    }

    // PO Box addresses are saveable, but never as the default.
    verifiedAddress.is_default = poBox
      ? false
      : existingAddress?.is_default ?? false;

    // StripeAddressForm's verified draft only carries postal fields — it
    // never sets is_shop_location. Preserve it from the address being
    // edited, exactly like is_default, so editing the business-location
    // address (even a plain typo fix) doesn't silently wipe the flag and
    // drop the seller out of calculate-tax's shop-location tax-eligibility
    // gate.
    verifiedAddress.is_shop_location = existingAddress?.is_shop_location ?? false;

    const nextAddresses = [...addresses];
    if (existingIndex >= 0) {
      nextAddresses[existingIndex] = verifiedAddress;
    } else {
      nextAddresses.push(verifiedAddress);
    }

    setSaving(true);
    try {
      await updateUserByKey(userDoc.uid, 'addresses', nextAddresses);
      await queryClient.invalidateQueries(['user']);
      dismiss(verifiedAddress);
    } catch {
      showErrorToast("We couldn't save this address. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex max-h-screen flex-col gap-[1.6rem] overflow-y-auto p-[2.4rem] sm:min-w-[40rem]">
      <StripeAddressForm
        mode="shipping"
        defaultValues={address}
        onChange={handleChange}
      />
      {confirmChoice && (
        <div className="flex flex-col gap-[1.2rem] rounded-[10px] bg-brand-secondary/5 p-[1.6rem]">
          {/* DRAFT COPY - Bill's own proposed working text (2026-08-21), still
              needs his verbatim sign-off before merge per the copy-approval
              rule. GB web#1743. */}
          <span className="text-[1.4rem]">
            {`We couldn't fully verify "${confirmChoice.typed.address_line1}" - Canada Post accepts this form, so you can save it as typed, or use the verified form below.`}
          </span>
          <button
            type="button"
            onClick={() => chooseAddress(confirmChoice.typed)}
            className="rounded-[8px] border border-brand-secondary p-[1.2rem] text-left text-[1.4rem] hover:bg-brand-secondary/10"
          >
            <span className="font-semibold">Save as typed: </span>
            {confirmChoice.typed.address_line1}, {confirmChoice.typed.city_locality},{' '}
            {confirmChoice.typed.state_province} {confirmChoice.typed.postal_code}
          </button>
          <button
            type="button"
            onClick={() => chooseAddress(confirmChoice.alternative)}
            className="rounded-[8px] border border-brand-secondary p-[1.2rem] text-left text-[1.4rem] hover:bg-brand-secondary/10"
          >
            <span className="font-semibold">Use verified form: </span>
            {confirmChoice.alternative.address_line1}, {confirmChoice.alternative.city_locality},{' '}
            {confirmChoice.alternative.state_province} {confirmChoice.alternative.postal_code}
          </button>
        </div>
      )}
      <div className="flex flex-col justify-end font-semibold text-brand-secondary sm:flex-row">
        <Button type="text" text="Cancel" onClick={() => dismiss()} />
        <Button
          type="secondary"
          text="Save Address"
          onClick={handleSubmit}
          loading={validating || saving}
          disabled={verifiedAddress === null || validating || saving}
        />
      </div>
    </div>
  );
};
type AddAddressModalProps = {
  isOpen: boolean;
  dismiss: (address?: AddressDocument) => void;
  address?: AddressDocument;
  index?: number;
  dontUpdateUserDoc?: boolean;
};
const AddAddressModal = ({
  isOpen,
  dismiss,
  address,
  index,
  dontUpdateUserDoc,
}: AddAddressModalProps) => {
  const { stripePromise } = useStripeContext();
  return (
    <BaseModal
      isOpen={isOpen}
      dismiss={() => dismiss()}
      title={
        <span className="p-[2.4rem] text-[2.4rem] font-semibold sm:p-0">
          {address ? 'Edit Address' : 'Add New Address'}
        </span>
      }
      isFullScreen={isMobile()}
      overflowType="visible"
    >
      <Elements stripe={stripePromise} options={STRIPE_ELEMENT_OPTIONS}>
        <AddressForm
          address={address}
          index={index}
          dismiss={(address) => dismiss(address)}
          dontUpdateUserDoc={dontUpdateUserDoc}
        />
      </Elements>
    </BaseModal>
  );
};

export default AddAddressModal;
